import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsTopCustomerRow } from "@shared-core/data/api-types";
import { TopCustomersChart } from "./TopCustomersChart";

// The Top 100 Customers tab: loads the composed
// /views/insights/top-customers series (server-ranked) and renders the
// scale/momentum scatter, revenue Pareto, and watch-list. Covers
// loading/empty/error, mirroring the Monthly-tab template.

type State =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      customers: InsightsTopCustomerRow[];
      companyRevenueTtm: number;
    };

// `tradingCompany` is a code chosen in the page header's Trading company
// control; absent is the group. Changing it refetches the ranking.
export function TopCustomersTab({
  tradingCompany,
}: {
  tradingCompany?: string;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getInsightsTopCustomers(
        undefined,
        tradingCompany,
      );
      setState({
        status: "ready",
        customers: result.customers,
        companyRevenueTtm: result.company_revenue_ttm,
      });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, tradingCompany]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading customers…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load customer data. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <TopCustomersChart
      customers={state.customers}
      companyRevenueTtm={state.companyRevenueTtm}
    />
  );
}
